import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Table, Loader, Tabs, Typography } from '@lucid/lego';
import type { FilterState } from '@lucid/lego';
import { useStore } from '../../store/store';
import {
  useGetInventoryVehicles,
  useGetLocation,
  useGetPrCodes,
  useGetCheckoutDetail,
  type ModelType,
} from '../../api/services/lupp/inventory/inventory';
import type { TransformedCheckoutDetail } from '../../api/services/lupp/inventory/transformers.checkout';
import {
  getInventoryColumns,
  transformInventoryData,
  filterStateToInventoryFilters,
  DEFAULT_FILTER_STATE,
  type InventoryTableRow,
} from './utils';
import ConfigureShortCode from './ConfigureShortCode/ConfigureShortCode';
import Checkout from '../Checkout/Checkout';
import styles from './Configurator.module.scss';

export type CheckoutParams = {
  shortCode: string;
  batchNumber: string;
  uuid: string;
  type: 'new' | 'demo' | 'pre-owned';
};

/**
 * Prospect details for quote creation
 */
export type ProspectDetails = {
  prospectId: string;
  prospectName?: string;
  quoteId?: string;
};

export type ConfiguratorProps = {
  /**
   * Callback when a vehicle is configured via short code
   */
  onConfigureVehicle?: (shortCode: string) => void;
  /**
   * Whether to show the "Configure with short code" tab
   * @default true
   */
  showConfigureTab?: boolean;
  /**
   * Custom data-testid for the component
   * @default 'configurator'
   */
  dataTestId?: string;
  /**
   * Callback when vehicle details view is shown/hidden
   */
  onDetailsViewChange?: (isShowingDetails: boolean) => void;
  /**
   * Checkout params from URL - when provided, show checkout view
   */
  checkoutParams?: CheckoutParams | null;
  /**
   * Callback when navigating to checkout view
   */
  onCheckoutNavigate?: (params: CheckoutParams) => void;
  /**
   * Callback when navigating back from checkout to inventory
   */
  onCheckoutBack?: () => void;
  /**
   * Prospect details for quote creation
   */
  prospectDetails?: ProspectDetails;
};

/**
 * Configurator component for browsing and filtering available inventory vehicles
 * @component
 * @param props - Component props
 * @param props.onConfigureVehicle - Callback when configuring via short code
 * @param props.showConfigureTab - Whether to show configure tab
 * @param props.dataTestId - Test ID for the component
 * @returns JSX.Element
 *
 * @example
 * <Configurator onConfigureVehicle={(code) => handleConfigure(code)} />
 */
const Configurator: React.FC<ConfiguratorProps> = ({
  onConfigureVehicle,
  showConfigureTab = true,
  dataTestId = 'configurator',
  onDetailsViewChange,
  checkoutParams,
  onCheckoutNavigate,
  onCheckoutBack,
  prospectDetails: externalProspectDetails,
}) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const { prospectId: urlProspectId } = useParams<{ prospectId?: string }>();

  const selectedProspect = useStore((state) => state.selectedProspect);
  // const [filters, setFilters] = useState<InventoryFilters>(initialFilters);
  // const [isFilterSliderOpen, setIsFilterSliderOpen] = useState(false);
  // const [visibleColumns, setVisibleColumns] = useState<string[]>(
  //   inventoryColumns.map((col) => col.key)
  // );

  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [selectedVehicle, setSelectedVehicle] = useState<InventoryTableRow | null>(null);
  const [checkoutData, setCheckoutData] = useState<TransformedCheckoutDetail | null>(null);
  // Track the last fetched checkout params to prevent duplicate fetches
  const lastFetchedParamsRef = useRef<string | null>(null);

  // Get prospectId from URL first, fallback to store
  const prospectId = urlProspectId ?? selectedProspect?.prospectIdentifier ?? '';
  const prospectDetails = externalProspectDetails ?? {
    prospectId,
  };

  // Checkout detail mutation
  const { mutateAsync: getCheckoutDetail, isPending: isLoadingDetails } =
    useGetCheckoutDetail(locale);

  // Fetch user location from IP
  const { data: location, isLoading: isLocationLoading } = useGetLocation();

  // Determine current model from filter state for prCodes fetch
  const selectedModels = filterState.model as string[] | undefined;
  const isGravitySelected = selectedModels?.includes('Gravity') ?? false;
  const currentModel: ModelType = isGravitySelected ? 'gravity' : 'air';
  // Only fetch prCodes when not in checkout view (check checkoutParams from URL)
  const { data: prCodes } = useGetPrCodes(currentModel, '2026', 'en-us', !checkoutParams);

  // Convert FilterState to InventoryFilters for API call
  const inventoryFilters = useMemo(
    () => filterStateToInventoryFilters(filterState, prCodes),
    [filterState, prCodes]
  );

  // Only fetch inventory vehicles when not in checkout view (check checkoutParams from URL)
  const { data, isLoading, error, isFetching } = useGetInventoryVehicles({
    lat: location?.lat ?? 0,
    long: location?.long ?? 0,
    zipState: location?.region ?? 'AZ',
    modelYear: '2025,2026',
    requestedVehiclesNum: 200,
    filters: inventoryFilters,
    enabled: !!location && !checkoutParams,
    locale,
  });

  // Track if we've loaded data at least once
  const hasData = data?.data !== undefined;

  const tableData = useMemo(() => {
    if (!data?.data) return [];
    return transformInventoryData(data.data);
  }, [data]);

  // Build columns with dynamic filter options from prCodes
  const selectedTrims = filterState.trim as string[] | undefined;
  const columns = useMemo(
    () => getInventoryColumns(prCodes, isGravitySelected, selectedTrims),
    [prCodes, isGravitySelected, selectedTrims]
  );

  // Handle checkout params from URL on mount/change
  useEffect(() => {
    const fetchCheckoutFromUrl = async () => {
      if (checkoutParams && !selectedVehicle && location) {
        // Create a unique key for these params to prevent duplicate fetches
        const paramsKey = `${checkoutParams.shortCode}-${checkoutParams.uuid}-${checkoutParams.batchNumber}`;

        // Skip if we've already fetched these params
        if (lastFetchedParamsRef.current === paramsKey) {
          return;
        }

        lastFetchedParamsRef.current = paramsKey;
        onDetailsViewChange?.(true);
        setCheckoutData(null);

        try {
          const details = await getCheckoutDetail({
            shortCode: checkoutParams.shortCode,
            batchNumber: checkoutParams.batchNumber,
            uuid: checkoutParams.uuid,
            salesMode: false,
            type: checkoutParams.type,
            zipState: location.region ?? 'CA',
          });
          setCheckoutData(details);

          // Create a minimal vehicle object from checkout params
          setSelectedVehicle({
            vin: '',
            webLink: '',
            batchNumber: checkoutParams.batchNumber,
            availability: '',
            model: '',
            trim: '',
            drivetrain: '',
            exteriorColor: '',
            exteriorColorCode: '',
            interiorColor: '',
            interiorColorCode: '',
            appearance: '',
            appearanceCode: '',
            roof: '',
            wheels: '',
            wheelsCode: '',
            year: '',
            adas: '',
            adasCode: '',
            soundSystem: '',
            soundSystemCode: '',
            frontSeats: '',
            frontSeatsCode: '',
            rearSeats: '',
            comfortPackage: '',
            packages: '',
            packagesCode: '',
            dynamicHandlingPackage: '',
            technologyPackage: '',
            towingPackage: '',
            uuid: checkoutParams.uuid,
            shortCode: checkoutParams.shortCode,
            condition: checkoutParams.type,
            distanceMiles: 0,
            price: 0,
            msrp: 0,
            internetPrice: 0,
          } as InventoryTableRow);
        } catch (err) {
          console.error('Error fetching checkout details from URL:', err);
        }
      }
    };
    fetchCheckoutFromUrl();
  }, [checkoutParams, getCheckoutDetail, location, onDetailsViewChange, selectedVehicle]);

  const handleVinClick = useCallback(
    async (vehicle: InventoryTableRow) => {
      // Use URL-based navigation if callback provided
      if (onCheckoutNavigate) {
        onCheckoutNavigate({
          shortCode: vehicle.shortCode,
          batchNumber: vehicle.batchNumber,
          uuid: vehicle.uuid,
          type: vehicle.condition,
        });
        return;
      }

      // Fallback to local state management
      setSelectedVehicle(vehicle);
      setCheckoutData(null);
      onDetailsViewChange?.(true);
      try {
        const details = await getCheckoutDetail({
          shortCode: vehicle.shortCode,
          batchNumber: vehicle.batchNumber,
          uuid: vehicle.uuid,
          salesMode: false,
          type: vehicle.condition,
          zipState: location?.region ?? 'CA',
        });
        setCheckoutData(details);
      } catch (err) {
        console.error('Error fetching checkout details:', err);
      }
    },
    [getCheckoutDetail, location?.region, onDetailsViewChange, onCheckoutNavigate]
  );

  // Add VIN click handler to columns
  const columnsWithVinClick = useMemo(() => {
    return columns.map((col) => {
      if (col.key === 'vin') {
        return {
          ...col,
          render: (_: unknown, record: InventoryTableRow) => (
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color="blue"
              onClick={() => handleVinClick(record)}
              className={styles.clickableVin}
            >
              {record.vin}
            </Typography>
          ),
        };
      }
      return col;
    });
  }, [columns, theme, handleVinClick]);

  const handleFilterChange = useCallback((newFilterState: FilterState) => {
    setFilterState(newFilterState);
  }, []);

  const handleConfigureVehicle = (shortCode: string) => {
    if (onConfigureVehicle) {
      onConfigureVehicle(shortCode);
    } else {
      console.error('Configure vehicle with short code:', shortCode);
    }
  };

  const handleBackToInventory = () => {
    setSelectedVehicle(null);
    setCheckoutData(null);
    onDetailsViewChange?.(false);
    onCheckoutBack?.();
  };

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  if (error) {
    return (
      <Box data-testid={`${dataTestId}-error`}>
        <Box pt={8}>Error loading inventory data</Box>
      </Box>
    );
  }

  // Show vehicle details view when a vehicle is selected
  if (selectedVehicle) {
    return (
      <Checkout
        vehicle={selectedVehicle}
        checkoutData={checkoutData}
        isLoading={isLoadingDetails}
        onBack={handleBackToInventory}
        dataTestId={`${dataTestId}-checkout`}
        prospectDetails={prospectDetails}
      />
    );
  }

  const availableVehiclesTab = {
    id: 'available-vehicles',
    label: 'Available Vehicles',
    content: (
      <div className={styles.inventoryBrowserContainer}>
        <div className={styles.tableWrapper}>
          {(isLoading || isLocationLoading) && !hasData ? (
            <Box className={styles.loaderWrapper}>
              <Loader />
            </Box>
          ) : (
            <div className={styles.tableContentWrapper}>
              {isFetching && (
                <div className={styles.fetchingOverlay}>
                  <Loader />
                </div>
              )}
              <Table
                columns={columnsWithVinClick}
                data={tableData}
                theme={invertedTheme}
                selection={{ enabled: false }}
                columnConfig={{ enabled: true }}
                filter={{
                  enabled: true,
                  state: filterState,
                  onChange: handleFilterChange,
                }}
                pagination={{
                  enabled: true,
                  pageSize: 10,
                }}
                search={{ placeholder: 'Search with VIN or Batch Number' }}
                emptyState={{ message: 'No vehicles match your filters' }}
                gaComponent={`${dataTestId}-table`}
                dataTestId={`${dataTestId}-table`}
              />
            </div>
          )}
        </div>
      </div>
    ),
  };

  const configureShortCodeTab = {
    id: 'configure-short-code',
    label: 'Configure with short code',
    content: <ConfigureShortCode onConfigureVehicle={handleConfigureVehicle} />,
  };

  const tabs = showConfigureTab
    ? [availableVehiclesTab, configureShortCodeTab]
    : [availableVehiclesTab];

  return (
    <Box data-testid={dataTestId}>
      <Tabs
        dataTestId={`${dataTestId}-tabs`}
        theme={theme}
        defaultActiveTab="available-vehicles"
        tabs={tabs}
      />
    </Box>
  );
};

export default Configurator;
